/**
 * Mind maps: an indented outline read as a tree, then laid out with no two ideas
 * overlapping.
 *
 * Three layouts. "Both sides" puts the central idea in the middle with its branches split
 * between the right (top to bottom) and the left (bottom to top), so the map reads
 * clockwise. "Tree" grows every branch to the right of the central idea. "Radial" puts
 * each level on a ring around the centre, each branch in its own slice.
 *
 * In the two tree layouts non-overlap holds by construction: every subtree owns a
 * horizontal band no other subtree enters, and every depth owns a column. The radial
 * layout gives each node a slice of angle in proportion to the leaves below it, then
 * pushes each ring outward until no box on it touches any box already placed.
 */

import {
  CHART_PADDING,
  LINE_HEIGHT,
  layoutTitle,
  type Rect,
  type TitleBlock,
} from "@/lib/tool-kit/charts/layout";
import {
  maxTextWidth,
  type TextStyle,
  wrapText,
} from "@/lib/tool-kit/charts/text";
import { RectIndex } from "@/lib/tool-kit/diagrams/geometry";

/* ----------------------------------------------------------------- outline */

export interface OutlineNode {
  text: string;
  /** 1-based line in the outline box. */
  line: number;
  children: OutlineNode[];
}

export interface Outline {
  root: OutlineNode | null;
  /** Every idea, the central one included. */
  count: number;
  /** Lines left out because the map reached `limit` ideas. */
  dropped: number;
}

const LINE_BREAK = /\r\n|\r|\n/;
const LEADING_SPACE = /^[ \t ]*/;
const BULLET =
  /^(?:[-*+•◦▪–—]|\d{1,3}[.)]|[A-Za-z][.)]|[IVXLCDM]{1,6}[.)]|[ivxlcdm]{1,6}[.)])\s+/;
const TAB_WIDTH = 4;

/** Indentation in columns, a tab counting as four. */
function indentOf(line: string): number {
  let columns = 0;
  for (const char of LEADING_SPACE.exec(line)?.[0] ?? "") {
    columns =
      char === "\t"
        ? (Math.floor(columns / TAB_WIDTH) + 1) * TAB_WIDTH
        : columns + 1;
  }
  return columns;
}

/** A line's words without its bullet or outline number ("-", "•", "1.", "a)", "IV."). */
export function stripBullet(text: string): string {
  return text.trim().replace(BULLET, "").trim();
}

/**
 * Read an outline: the first line is the central idea, and every later line hangs under
 * the nearest line above it that is indented less. Lines indented the least become the
 * main branches, whether or not they are indented at all.
 */
export function parseOutline(text: string, limit = 120): Outline {
  let root: OutlineNode | null = null;
  const stack: { node: OutlineNode; indent: number }[] = [];
  let count = 0;
  let dropped = 0;
  for (const [index, raw] of text.split(LINE_BREAK).entries()) {
    const words = stripBullet(raw);
    if (words === "") {
      continue;
    }
    if (count >= limit) {
      dropped += 1;
      continue;
    }
    const node: OutlineNode = { text: words, line: index + 1, children: [] };
    count += 1;
    if (!root) {
      root = node;
      stack.push({ node, indent: Number.NEGATIVE_INFINITY });
      continue;
    }
    const indent = indentOf(raw);
    while (stack.length > 1 && (stack.at(-1)?.indent ?? 0) >= indent) {
      stack.pop();
    }
    stack.at(-1)?.node.children.push(node);
    stack.push({ node, indent });
  }
  return { root, count, dropped };
}

/** The outline written back with two spaces per level — what "Copy outline" copies. */
export function outlineText(root: OutlineNode | null): string {
  if (!root) {
    return "";
  }
  const lines: string[] = [];
  const walk = (node: OutlineNode, depth: number) => {
    lines.push(`${"  ".repeat(depth)}${node.text}`);
    for (const child of node.children) {
      walk(child, depth + 1);
    }
  };
  walk(root, 0);
  return lines.join("\n");
}

/* ------------------------------------------------------------------ layout */

export type MindMapLayoutKind = "both" | "tree" | "radial";

export interface MindNode {
  id: number;
  parent: number | null;
  depth: number;
  /** Index of the main branch the node belongs to; −1 for the central idea. */
  branch: number;
  lines: string[];
  fontSize: number;
  bold: boolean;
  rect: Rect;
}

export interface MindLink {
  from: number;
  to: number;
  /** A cubic Bézier: start, two control points, end. */
  points: [number, number, number, number, number, number, number, number];
}

export interface MindMapLayout {
  width: number;
  height: number;
  title: TitleBlock | null;
  nodes: MindNode[];
  links: MindLink[];
}

const NODE_STYLE = [
  { fontSize: 15, bold: true, maxWidth: 150, padX: 12, padY: 8 },
  { fontSize: 13, bold: true, maxWidth: 140, padX: 10, padY: 6 },
  { fontSize: 12, bold: false, maxWidth: 130, padX: 8, padY: 5 },
] as const;
const MAX_LINES = 4;
const H_GAP = 44;
const V_GAP = 10;
const MIN_WIDTH = 640;

interface Sized {
  id: number;
  depth: number;
  branch: number;
  parent: number | null;
  lines: string[];
  fontSize: number;
  bold: boolean;
  width: number;
  height: number;
  children: Sized[];
  /** Height of the band the subtree occupies (tree layouts). */
  band: number;
  /** Leaves below (radial layout). */
  leaves: number;
}

function sizeTree(root: OutlineNode): Sized {
  let next = 0;
  const visit = (
    node: OutlineNode,
    depth: number,
    branch: number,
    parent: number | null
  ): Sized => {
    const look = NODE_STYLE[Math.min(depth, NODE_STYLE.length - 1)];
    const style: TextStyle = { fontSize: look.fontSize, bold: look.bold };
    const lines = wrapText(node.text, look.maxWidth, style, MAX_LINES);
    const id = next++;
    const children = node.children.map((child, index) =>
      visit(child, depth + 1, depth === 0 ? index : branch, id)
    );
    const width = Math.ceil(maxTextWidth(lines, style)) + 2 * look.padX;
    const height =
      Math.ceil(lines.length * look.fontSize * LINE_HEIGHT) + 2 * look.padY;
    const span =
      children.reduce((sum, child) => sum + child.band, 0) +
      V_GAP * Math.max(0, children.length - 1);
    return {
      id,
      depth,
      branch,
      parent,
      lines,
      fontSize: look.fontSize,
      bold: look.bold,
      width,
      height,
      children,
      band: Math.max(height, span),
      leaves: Math.max(
        1,
        children.reduce((sum, child) => sum + child.leaves, 0)
      ),
    };
  };
  return visit(root, 0, -1, null);
}

function nodeOf(sized: Sized, rect: Rect): MindNode {
  return {
    id: sized.id,
    parent: sized.parent,
    depth: sized.depth,
    branch: sized.branch,
    lines: sized.lines,
    fontSize: sized.fontSize,
    bold: sized.bold,
    rect,
  };
}

/** Widest node at each depth below the root, across the given subtrees. */
function columnWidths(subtrees: readonly Sized[]): number[] {
  const widths: number[] = [];
  const walk = (node: Sized) => {
    widths[node.depth] = Math.max(widths[node.depth] ?? 0, node.width);
    for (const child of node.children) {
      walk(child);
    }
  };
  for (const subtree of subtrees) {
    walk(subtree);
  }
  return widths;
}

/**
 * Lay out subtrees in a column stack growing away from the root: to the right
 * (`side` 1) from `edge`, or to the left (`side` −1). Each subtree fills its own band of
 * height `band`, top to bottom from `top`.
 */
function placeSide(
  subtrees: readonly Sized[],
  edge: number,
  side: 1 | -1,
  top: number,
  out: Map<number, Rect>
): void {
  const widths = columnWidths(subtrees);
  const columnStart: number[] = [];
  let reach = edge + side * H_GAP;
  for (let depth = 1; depth < widths.length; depth++) {
    columnStart[depth] = reach;
    reach += side * ((widths[depth] ?? 0) + H_GAP);
  }
  const place = (node: Sized, bandTop: number): number => {
    const span =
      node.children.reduce((sum, child) => sum + child.band, 0) +
      V_GAP * Math.max(0, node.children.length - 1);
    let childTop = bandTop + (node.band - span) / 2;
    const centers: number[] = [];
    for (const child of node.children) {
      centers.push(place(child, childTop));
      childTop += child.band + V_GAP;
    }
    const middle =
      centers.length > 0
        ? ((centers[0] ?? 0) + (centers.at(-1) ?? 0)) / 2
        : bandTop + node.band / 2;
    const cy = Math.min(
      Math.max(middle, bandTop + node.height / 2),
      bandTop + node.band - node.height / 2
    );
    const start = columnStart[node.depth] ?? edge;
    out.set(node.id, {
      x: side === 1 ? start : start - node.width,
      y: cy - node.height / 2,
      width: node.width,
      height: node.height,
    });
    return cy;
  };
  let bandTop = top;
  for (const subtree of subtrees) {
    place(subtree, bandTop);
    bandTop += subtree.band + V_GAP;
  }
}

function sideHeight(subtrees: readonly Sized[]): number {
  return (
    subtrees.reduce((sum, subtree) => sum + subtree.band, 0) +
    V_GAP * Math.max(0, subtrees.length - 1)
  );
}

/**
 * Split the main branches between right and left so the two sides are as close to the
 * same height as possible, keeping their order: the first `k` go right (top to bottom),
 * the rest left (bottom to top), which reads clockwise.
 */
export function splitBranches(bands: readonly number[]): number {
  if (bands.length <= 1) {
    return bands.length;
  }
  const total = bands.reduce((sum, band) => sum + band, 0);
  let best = 1;
  let bestGap = Number.POSITIVE_INFINITY;
  let running = 0;
  for (let k = 1; k < bands.length; k++) {
    running += bands[k - 1];
    const gap = Math.abs(total - 2 * running);
    if (gap < bestGap) {
      best = k;
      bestGap = gap;
    }
  }
  return best;
}

function layoutTrees(root: Sized, kind: "both" | "tree"): Map<number, Rect> {
  const rects = new Map<number, Rect>();
  const branches = root.children;
  const split =
    kind === "both"
      ? splitBranches(branches.map((b) => b.band))
      : branches.length;
  const right = branches.slice(0, split);
  const left = branches.slice(split).reverse();
  const height = Math.max(root.height, sideHeight(right), sideHeight(left));
  const middle = height / 2;
  rects.set(root.id, {
    x: -root.width / 2,
    y: middle - root.height / 2,
    width: root.width,
    height: root.height,
  });
  placeSide(right, root.width / 2, 1, middle - sideHeight(right) / 2, rects);
  placeSide(left, -root.width / 2, -1, middle - sideHeight(left) / 2, rects);
  return rects;
}

const RING_STEP = 6;
const RING_GAP = 12;

/** Each node's angle: the middle of a slice proportional to the leaves under it. */
function radialAngles(root: Sized): Map<number, number> {
  const angles = new Map<number, number>();
  const assign = (node: Sized, from: number, to: number) => {
    angles.set(node.id, (from + to) / 2);
    let start = from;
    for (const child of node.children) {
      const share = ((to - from) * child.leaves) / node.leaves;
      assign(child, start, start + share);
      start += share;
    }
  };
  // Start at 12 o'clock and go clockwise (y grows downward).
  assign(root, -Math.PI / 2, (3 * Math.PI) / 2);
  return angles;
}

function centredRect(node: Sized, x: number, y: number): Rect {
  return {
    x: x - node.width / 2,
    y: y - node.height / 2,
    width: node.width,
    height: node.height,
  };
}

function layoutRadial(root: Sized): Map<number, Rect> {
  const rects = new Map<number, Rect>();
  const angles = radialAngles(root);
  const index = new RectIndex(48);
  const rootRect = centredRect(root, 0, 0);
  rects.set(root.id, rootRect);
  index.add(rootRect);
  let ring: Sized[] = root.children;
  let radius = Math.hypot(root.width, root.height) / 2;
  while (ring.length > 0) {
    const nodes = ring;
    const outer = Math.max(
      ...nodes.map((node) => Math.hypot(node.width, node.height) / 2)
    );
    radius += outer + RING_GAP;
    const placeAt = (r: number) =>
      nodes.map((node) => {
        const angle = angles.get(node.id) ?? 0;
        return centredRect(node, r * Math.cos(angle), r * Math.sin(angle));
      });
    let placed = placeAt(radius);
    for (let tries = 0; tries < 600 && !ringIsClear(placed, index); tries++) {
      radius += RING_STEP;
      placed = placeAt(radius);
    }
    for (const [i, node] of nodes.entries()) {
      rects.set(node.id, placed[i]);
      index.add(placed[i]);
    }
    radius += outer;
    ring = nodes.flatMap((node) => node.children);
  }
  return rects;
}

/** True when the ring's boxes touch neither each other nor anything placed before. */
function ringIsClear(placed: readonly Rect[], index: RectIndex): boolean {
  const own = new RectIndex(48);
  for (const rect of placed) {
    if (index.collides(rect, RING_GAP) || own.collides(rect, RING_GAP)) {
      return false;
    }
    own.add(rect);
  }
  return true;
}

function flatten(root: Sized): Sized[] {
  const all: Sized[] = [];
  const walk = (node: Sized) => {
    all.push(node);
    for (const child of node.children) {
      walk(child);
    }
  };
  walk(root);
  return all;
}

function link(
  kind: MindMapLayoutKind,
  parent: Rect,
  child: Rect
): MindLink["points"] {
  const pcx = parent.x + parent.width / 2;
  const pcy = parent.y + parent.height / 2;
  const ccx = child.x + child.width / 2;
  const ccy = child.y + child.height / 2;
  if (kind === "radial") {
    return [pcx, pcy, pcx, pcy, ccx, ccy, ccx, ccy];
  }
  const toRight = ccx > pcx;
  const x1 = toRight ? parent.x + parent.width : parent.x;
  const x2 = toRight ? child.x : child.x + child.width;
  const middle = (x1 + x2) / 2;
  return [x1, pcy, middle, pcy, middle, ccy, x2, ccy];
}

/** Lay out a mind map from its outline's root. */
export function layoutMindMap(
  root: OutlineNode,
  kind: MindMapLayoutKind,
  title: string
): MindMapLayout {
  const sized = sizeTree(root);
  const rects =
    kind === "radial" ? layoutRadial(sized) : layoutTrees(sized, kind);
  const all = flatten(sized);
  const boxes = [...rects.values()];
  const minX = Math.min(...boxes.map((r) => r.x));
  const maxX = Math.max(...boxes.map((r) => r.x + r.width));
  const minY = Math.min(...boxes.map((r) => r.y));
  const maxY = Math.max(...boxes.map((r) => r.y + r.height));
  const contentWidth = maxX - minX;
  const width = Math.ceil(
    Math.max(MIN_WIDTH, contentWidth + 2 * CHART_PADDING)
  );
  const titleBlock = layoutTitle(title, width);
  const top = CHART_PADDING + (titleBlock?.height ?? 0) + 4;
  const shiftX = (width - contentWidth) / 2 - minX;
  const shiftY = top - minY;
  const moved = (rect: Rect): Rect => ({
    ...rect,
    x: rect.x + shiftX,
    y: rect.y + shiftY,
  });
  const nodes = all.map((node) =>
    nodeOf(
      node,
      moved(rects.get(node.id) ?? { x: 0, y: 0, width: 0, height: 0 })
    )
  );
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const links: MindLink[] = [];
  for (const node of nodes) {
    const parent = node.parent === null ? undefined : byId.get(node.parent);
    if (parent) {
      links.push({
        from: parent.id,
        to: node.id,
        points: link(kind, parent.rect, node.rect),
      });
    }
  }
  return {
    width,
    height: Math.ceil(top + (maxY - minY) + CHART_PADDING),
    title: titleBlock,
    nodes,
    links,
  };
}
